/**
 * SAGA Audit Trail Viewer - Immutable Life-Critical Operation History
 *
 * Displays immutable audit trails for SAGA operations across all tenants.
 * ALL data comes from tenant-isolated audit repositories maintaining
 * domain-agnostic, data-driven architecture.
 *
 * LIFE-CRITICAL: This component provides compliance-grade audit trails
 * required for healthcare (HIPAA), financial (SOX), and legal domains.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Group,
  Text,
  Badge,
  Stack,
  ActionIcon,
  Alert,
  Table,
  Timeline,
  ThemeIcon,
  Tooltip,
  Select,
  TextInput,
  Button,
  Modal,
  JsonInput,
  ScrollArea,
  Divider,
  Code,
  Pagination,
  Switch,
  Container,
  Title,
  Flex
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertTriangle,
  IconFileAnalytics,
  IconCalendar,
  IconCircleCheck,
  IconClock,
  IconDatabase,
  IconDownload,
  IconEye,
  IconFilter,
  IconFingerprint,
  IconLock,
  IconRefresh,
  IconSearch,
  IconShield,
  IconTimeline,
  IconUser,
  IconX
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import { useAuditLogger } from '../hooks/useAuditLogger';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ErrorBoundary } from '../error/ErrorBoundary';

// GraphQL Queries (data-driven from immutable audit repository)
const GET_SAGA_AUDIT_TRAIL = gql`
  query GetSagaAuditTrail(
    $tenantId: String!
    $sagaExecutionId: String
    $startDate: String
    $endDate: String
    $operationType: String
    $limit: Int
    $offset: Int
  ) {
    sagaAuditTrail(
      sagaExecutionId: $sagaExecutionId
      startDate: $startDate
      endDate: $endDate
      operationType: $operationType
      limit: $limit
      offset: $offset
    ) {
      entries {
        entryId
        correlationId
        sagaExecutionId
        tenantId
        initiator
        operationType
        primitiveType
        operation
        payload
        result
        timestamp
        duration
        status
        errorMessage
        immutableHash
        auditMetadata
      }
      totalCount
      hasNextPage
      hasPreviousPage
    }
  }
`;

const GET_AUDIT_ENTRY_VERIFICATION = gql`
  query GetAuditEntryVerification($tenantId: String!, $entryId: String!) {
    auditEntryVerification(entryId: $entryId) {
      entryId
      isValid
      hashVerification
      timestampVerification
      signatureVerification
      chainIntegrity
      complianceFlags
      verificationTimestamp
    }
  }
`;

const GET_AUDIT_COMPLIANCE_REPORT = gql`
  query GetAuditComplianceReport(
    $tenantId: String!
    $startDate: String!
    $endDate: String!
    $reportType: String!
  ) {
    auditComplianceReport(
      startDate: $startDate
      endDate: $endDate
      reportType: $reportType
    ) {
      reportId
      reportType
      generatedAt
      totalEntries
      verifiedEntries
      complianceScore
      violations
      recommendations
      exportUrl
    }
  }
`;

// Type definitions (data-driven from audit repository)
interface AuditEntry {
  entryId: string;
  correlationId: string;
  sagaExecutionId: string;
  tenantId: string;
  initiator: string;
  operationType: string;
  primitiveType?: string; // One of 16 FERP primitive types
  operation: string;
  payload: any;
  result?: any;
  timestamp: string;
  duration?: number;
  status: 'SUCCESS' | 'FAILED' | 'PENDING' | 'COMPENSATED';
  errorMessage?: string;
  immutableHash: string;
  auditMetadata: any;
}

interface AuditVerification {
  entryId: string;
  isValid: boolean;
  hashVerification: boolean;
  timestampVerification: boolean;
  signatureVerification: boolean;
  chainIntegrity: boolean;
  complianceFlags: string[];
  verificationTimestamp: string;
}

interface ComplianceReport {
  reportId: string;
  reportType: string;
  generatedAt: string;
  totalEntries: number;
  verifiedEntries: number;
  complianceScore: number;
  violations: string[];
  recommendations: string[];
  exportUrl: string;
}

// Operation type color mapping for life-critical visualization
const getOperationTypeColor = (operationType: string): string => {
  switch (operationType) {
    case 'saga_execution_started': return 'blue';
    case 'saga_state_transition': return 'cyan';
    case 'saga_step_executed': return 'green';
    case 'saga_compensation': return 'orange';
    case 'saga_completed': return 'teal';
    case 'saga_failed': return 'red';
    case 'emergency_escalation': return 'pink';
    default: return 'gray';
  }
};

// Status icon mapping
const getStatusIcon = (status: string): React.ReactNode => {
  switch (status) {
    case 'SUCCESS': return <IconCircleCheck size={16} color="green" />;
    case 'FAILED': return <IconX size={16} color="red" />;
    case 'PENDING': return <IconClock size={16} color="orange" />;
    case 'COMPENSATED': return <IconRefresh size={16} color="cyan" />;
    default: return <IconClock size={16} color="gray" />;
  }
};

// Primitive type icons (domain-agnostic)
const getPrimitiveIcon = (primitiveType?: string): React.ReactNode => {
  if (!primitiveType) return <IconDatabase size={16} />;

  const iconMap: Record<string, React.ReactNode> = {
    wfl: <IconTimeline size={16} />,
    ins: <IconDatabase size={16} />,
    sig: <IconFingerprint size={16} />,
    exe: <IconCircleCheck size={16} />,
    mon: <IconEye size={16} />,
    gov: <IconShield size={16} />,
    pol: <IconShield size={16} />,
    esc: <IconAlertTriangle size={16} />,
    not: <IconUser size={16} />,
    met: <IconDatabase size={16} />,
    aud: <IconLock size={16} />,
    rep: <IconDownload size={16} />,
    ana: <IconSearch size={16} />,
    opt: <IconTimeline size={16} />,
    rel: <IconCircleCheck size={16} />,
    lif: <IconShield size={16} />
  };
  return iconMap[primitiveType] || <IconDatabase size={16} />;
};

export const SagaAuditTrailViewer: React.FC<{
  sagaExecutionId?: string;
  autoRefresh?: boolean;
}> = ({ sagaExecutionId: propSagaExecutionId, autoRefresh = false }) => {
  const { currentTenant } = useCurrentTenant();
  const { logUserAction } = useAuditLogger();

  // State management
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);

  // Filters
  const [sagaExecutionId, setSagaExecutionId] = useState(propSagaExecutionId || '');
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 24 * 60 * 60 * 1000) // Default: last 24 hours
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [operationType, setOperationType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [showImmutableHashes, setShowImmutableHashes] = useState(false);

  // Data queries (all from immutable audit repository)
  const {
    data: auditData,
    loading: auditLoading,
    error: auditError,
    refetch: refetchAudit
  } = useQuery<Record<string, any>>(GET_SAGA_AUDIT_TRAIL, {
    variables: {
      tenantId: currentTenant?.tenantId,
      sagaExecutionId: sagaExecutionId || undefined,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      operationType: operationType === 'all' ? undefined : operationType,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize
    },
    pollInterval: autoRefresh ? 5000 : 0,
    skip: !currentTenant?.tenantId
  });

  const {
    data: verificationData,
    loading: verificationLoading
  } = useQuery<Record<string, any>>(GET_AUDIT_ENTRY_VERIFICATION, {
    variables: {
      tenantId: currentTenant?.tenantId,
      entryId: selectedEntry?.entryId
    },
    skip: !currentTenant?.tenantId || !selectedEntry?.entryId
  });

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetchAudit();
      }, 5000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefresh, refetchAudit]);

  // Filter audit entries
  const filteredEntries = useMemo(() => {
    if (!auditData?.sagaAuditTrail?.entries) return [];

    return auditData.sagaAuditTrail.entries.filter((entry: AuditEntry) => {
      const matchesSearch = searchTerm === '' ||
        entry.correlationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.initiator.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.operation.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [auditData, searchTerm]);

  // Handle entry selection
  const handleEntryClick = useCallback(async (entry: AuditEntry) => {
    setSelectedEntry(entry);
    setShowEntryModal(true);

    // Log audit access for compliance
    await logUserAction({
      action: 'audit_entry_accessed',
      details: {
        targetId: entry.entryId,
        sagaExecutionId: entry.sagaExecutionId,
        operationType: entry.operationType
      },
      metadata: {
        tenantId: currentTenant?.tenantId
      }
    });
  }, [logUserAction, currentTenant?.tenantId]);

  // Handle verification check
  const handleVerificationCheck = useCallback((entry: AuditEntry) => {
    setSelectedEntry(entry);
    setShowVerificationModal(true);
  }, []);

  // Export compliance report
  const handleExportReport = useCallback(async () => {
    if (!startDate || !endDate) {
      notifications.show({
        title: 'Export Failed',
        message: 'Please select date range for compliance report',
        color: 'red'
      });
      return;
    }

    try {
      // This would trigger the compliance report generation
      notifications.show({
        title: 'Compliance Report',
        message: 'Report generation started. You will be notified when complete.',
        color: 'blue'
      });

      // Log compliance report generation
      await logUserAction({
        action: 'compliance_report_generated',
        metadata: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          tenantId: currentTenant?.tenantId
        }
      });

    } catch (error) {
      console.error('Report export failed:', error);
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to generate compliance report',
        color: 'red'
      });
    }
  }, [startDate, endDate, logUserAction, currentTenant?.tenantId]);

  // Render audit trail table
  const renderAuditTable = () => (
    <ScrollArea h={600}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Timestamp</Table.Th>
            <Table.Th>Operation</Table.Th>
            <Table.Th>Primitive</Table.Th>
            <Table.Th>Initiator</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Duration</Table.Th>
            {showImmutableHashes && <Table.Th>Hash</Table.Th>}
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredEntries.map((entry: AuditEntry) => (
            <Table.Tr
              key={entry.entryId}
              style={{ cursor: 'pointer' }}
              onClick={() => handleEntryClick(entry)}
            >
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {new Date(entry.timestamp).toLocaleString()}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Badge
                    color={getOperationTypeColor(entry.operationType)}
                    size="sm"
                  >
                    {entry.operationType}
                  </Badge>
                  <Text size="sm">{entry.operation}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                {entry.primitiveType && (
                  <Group gap="xs">
                    {getPrimitiveIcon(entry.primitiveType)}
                    <Text size="sm">{entry.primitiveType}</Text>
                  </Group>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{entry.initiator}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {getStatusIcon(entry.status)}
                  <Text size="sm">{entry.status}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {entry.duration ? `${entry.duration}ms` : '-'}
                </Text>
              </Table.Td>
              {showImmutableHashes && (
                <Table.Td>
                  <Code>
                    {entry.immutableHash.substring(0, 8)}...
                  </Code>
                </Table.Td>
              )}
              <Table.Td>
                <Group gap="xs">
                  <ActionIcon
                    size="sm"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleVerificationCheck(entry);
                    }}
                  >
                    <IconShield size={14} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );

  // Render entry details modal
  const renderEntryModal = () => (
    <Modal
      opened={showEntryModal}
      onClose={() => setShowEntryModal(false)}
      title="Audit Entry Details"
      size="xl"
    >
      {selectedEntry && (
        <Stack>
          <Group justify="space-between">
            <Text fw={500}>Entry ID: {selectedEntry.entryId}</Text>
            <Badge color={getOperationTypeColor(selectedEntry.operationType)}>
              {selectedEntry.operationType}
            </Badge>
          </Group>

          <Divider />

          <Group>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Timestamp</Text>
              <Text ff="monospace">
                {new Date(selectedEntry.timestamp).toLocaleString()}
              </Text>
            </Box>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Status</Text>
              <Group gap="xs">
                {getStatusIcon(selectedEntry.status)}
                <Text>{selectedEntry.status}</Text>
              </Group>
            </Box>
          </Group>

          <Group>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Initiator</Text>
              <Text>{selectedEntry.initiator}</Text>
            </Box>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Correlation ID</Text>
              <Code>{selectedEntry.correlationId}</Code>
            </Box>
          </Group>

          {selectedEntry.primitiveType && (
            <Group>
              <Box flex={1}>
                <Text size="sm" color="dimmed">Primitive Type</Text>
                <Group gap="xs">
                  {getPrimitiveIcon(selectedEntry.primitiveType)}
                  <Text>{selectedEntry.primitiveType}</Text>
                </Group>
              </Box>
              <Box flex={1}>
                <Text size="sm" color="dimmed">Operation</Text>
                <Text>{selectedEntry.operation}</Text>
              </Box>
            </Group>
          )}

          <Box>
            <Text size="sm" color="dimmed" mb="xs">Immutable Hash</Text>
            <Code block>{selectedEntry.immutableHash}</Code>
          </Box>

          {selectedEntry.payload && (
            <Box>
              <Text size="sm" color="dimmed" mb="xs">Payload</Text>
              <JsonInput
                value={JSON.stringify(selectedEntry.payload, null, 2)}
                readOnly
                autosize
                minRows={4}
                maxRows={8}
              />
            </Box>
          )}

          {selectedEntry.result && (
            <Box>
              <Text size="sm" color="dimmed" mb="xs">Result</Text>
              <JsonInput
                value={JSON.stringify(selectedEntry.result, null, 2)}
                readOnly
                autosize
                minRows={4}
                maxRows={8}
              />
            </Box>
          )}

          {selectedEntry.errorMessage && (
            <Alert color="red" icon={<IconAlertTriangle />}>
              <Text size="sm">{selectedEntry.errorMessage}</Text>
            </Alert>
          )}
        </Stack>
      )}
    </Modal>
  );

  // Render verification modal
  const renderVerificationModal = () => (
    <Modal
      opened={showVerificationModal}
      onClose={() => setShowVerificationModal(false)}
      title="Audit Entry Verification"
      size="md"
    >
      {verificationLoading ? (
        <LoadingOverlay />
      ) : verificationData?.auditEntryVerification ? (
        <Stack>
          <Alert
            color={verificationData.auditEntryVerification.isValid ? 'green' : 'red'}
            icon={verificationData.auditEntryVerification.isValid ?
              <IconCircleCheck /> : <IconAlertTriangle />}
          >
            Entry is {verificationData.auditEntryVerification.isValid ? 'VALID' : 'INVALID'}
          </Alert>

          <Group>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Hash Verification</Text>
              <Group gap="xs">
                {verificationData.auditEntryVerification.hashVerification ?
                  <IconCircleCheck size={16} color="green" /> :
                  <IconX size={16} color="red" />}
                <Text size="sm">
                  {verificationData.auditEntryVerification.hashVerification ? 'Valid' : 'Invalid'}
                </Text>
              </Group>
            </Box>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Timestamp Verification</Text>
              <Group gap="xs">
                {verificationData.auditEntryVerification.timestampVerification ?
                  <IconCircleCheck size={16} color="green" /> :
                  <IconX size={16} color="red" />}
                <Text size="sm">
                  {verificationData.auditEntryVerification.timestampVerification ? 'Valid' : 'Invalid'}
                </Text>
              </Group>
            </Box>
          </Group>

          <Group>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Signature Verification</Text>
              <Group gap="xs">
                {verificationData.auditEntryVerification.signatureVerification ?
                  <IconCircleCheck size={16} color="green" /> :
                  <IconX size={16} color="red" />}
                <Text size="sm">
                  {verificationData.auditEntryVerification.signatureVerification ? 'Valid' : 'Invalid'}
                </Text>
              </Group>
            </Box>
            <Box flex={1}>
              <Text size="sm" color="dimmed">Chain Integrity</Text>
              <Group gap="xs">
                {verificationData.auditEntryVerification.chainIntegrity ?
                  <IconCircleCheck size={16} color="green" /> :
                  <IconX size={16} color="red" />}
                <Text size="sm">
                  {verificationData.auditEntryVerification.chainIntegrity ? 'Valid' : 'Invalid'}
                </Text>
              </Group>
            </Box>
          </Group>

          {verificationData.auditEntryVerification.complianceFlags.length > 0 && (
            <Box>
              <Text size="sm" color="dimmed" mb="xs">Compliance Flags</Text>
              <Group gap="xs">
                {verificationData.auditEntryVerification.complianceFlags.map((flag: string) => (
                  <Badge key={flag} color="orange" size="sm">{flag}</Badge>
                ))}
              </Group>
            </Box>
          )}

          <Text size="xs" color="dimmed">
            Verified at: {new Date(verificationData.auditEntryVerification.verificationTimestamp).toLocaleString()}
          </Text>
        </Stack>
      ) : (
        <Text>No verification data available</Text>
      )}
    </Modal>
  );

  // Handle loading and error states
  if (auditLoading) {
    return <LoadingOverlay />;
  }

  if (auditError) {
    return (
      <Alert color="red" icon={<IconAlertTriangle />}>
        Failed to load audit trail: {auditError.message}
      </Alert>
    );
  }

  const totalPages = Math.ceil((auditData?.sagaAuditTrail?.totalCount || 0) / pageSize);

  return (
    <ErrorBoundary>
      <Container size="xl">
        <Group justify="space-between" mb="lg">
          <Title order={3}>SAGA Audit Trail</Title>
          <Group>
            <Badge color="red">IMMUTABLE</Badge>
            <Badge color="blue">{currentTenant?.domain || 'System'}</Badge>
          </Group>
        </Group>

        {/* Filters */}
        <Card mb="md">
          <Stack>
            <Group>
              <TextInput
                placeholder="SAGA Execution ID"
                value={sagaExecutionId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSagaExecutionId(e.target.value)}
                leftSection={<IconSearch size={16} />}
              />
              <TextInput
                placeholder="Search operations..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                leftSection={<IconFilter size={16} />}
              />
              <Select
                data={[
                  { value: 'all', label: 'All Operations' },
                  { value: 'saga_execution_started', label: 'Execution Started' },
                  { value: 'saga_state_transition', label: 'State Transition' },
                  { value: 'saga_step_executed', label: 'Step Executed' },
                  { value: 'saga_compensation', label: 'Compensation' },
                  { value: 'saga_completed', label: 'Completed' },
                  { value: 'saga_failed', label: 'Failed' },
                  { value: 'emergency_escalation', label: 'Emergency' }
                ]}
                value={operationType}
                onChange={(value: string | null) => setOperationType(value || 'all')}
              />
            </Group>

            <Group>
              <DatePickerInput
                label="Start Date"
                value={startDate}
                onChange={(value: string | null) => { setStartDate(value ? new Date(value) : null); }}
                leftSection={<IconCalendar size={16} />}
              />
              <DatePickerInput
                label="End Date"
                value={endDate}
                onChange={(value: string | null) => { setEndDate(value ? new Date(value) : null); }}
                leftSection={<IconCalendar size={16} />}
              />
              <Switch
                label="Show Hashes"
                checked={showImmutableHashes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowImmutableHashes(e.currentTarget.checked)}
              />
            </Group>

            <Group justify="space-between">
              <Group>
                <ActionIcon onClick={() => refetchAudit()}>
                  <IconRefresh size={16} />
                </ActionIcon>
                <Text size="sm" color="dimmed">
                  {auditData?.sagaAuditTrail?.totalCount || 0} entries found
                </Text>
              </Group>
              <Group>
                <Button
                  leftSection={<IconDownload size={16} />}
                  variant="outline"
                  onClick={handleExportReport}
                >
                  Export Compliance Report
                </Button>
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Audit Table */}
        {renderAuditTable()}

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex justify="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
              boundaries={1}
              siblings={1}
            />
          </Flex>
        )}

        {/* Modals */}
        {renderEntryModal()}
        {renderVerificationModal()}

        {/* Life-Critical Alert */}
        {filteredEntries.some((entry: AuditEntry) => entry.status === 'FAILED') && (
          <Alert
            color="red"
            icon={<IconAlertTriangle />}
            style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
          >
            LIFE-CRITICAL: Failed operations detected in audit trail
          </Alert>
        )}
      </Container>
    </ErrorBoundary>
  );
};